/**
 * The shape of the frontend's configuration.
 *
 * Deliberately free of any `import.meta.env` access so it can be imported from
 * two very different places: the browser bundle (src/config/env.ts) and the
 * Vite config running in Node at build time. One schema, checked twice — once
 * before the bundle is produced and once when it boots — with no chance of the
 * two definitions drifting apart.
 */
import { z } from 'zod';

export const envSchema = z.object({
  /** Base URL of kdoshr-api. Empty in development, where Vite proxies /api. */
  VITE_API_URL: z.string().default(''),

  /**
   * Which deployed environment this bundle is for.
   *
   * 'dev' is kdoshr-infra's environments/dev, serving dev.kdoshr.com — a real
   * deployment, not a developer machine. 'local' is the machine. They are
   * different things and the names are not interchangeable.
   *
   * Spelled as the infrastructure spells it: kdoshr-infra passes
   * VITE_ENVIRONMENT="$ENV" straight from its deploy script, where the
   * environment is 'dev' or 'prod'. 'development'/'production' would fail the
   * build here, which is the intent — this schema is validated in
   * vite.config.ts at build time as well as at boot.
   */
  VITE_ENVIRONMENT: z.enum(['local', 'dev', 'prod']).default('local'),

  /** Optional: only set once error reporting is provisioned. */
  VITE_SENTRY_DSN: z.string().optional(),

  /** Shown in the UI and attached to error reports, so a bug maps to a build. */
  VITE_APP_VERSION: z.string().default('dev'),

  VITE_ENABLE_DEVTOOLS: z
    .string()
    .default('false')
    .transform((value) => value === 'true'),

  /**
   * Whether the notifications WebSocket may connect.
   *
   * Off by default on the developer machine: the local `runserver` is WSGI and
   * serves no WebSockets, so an always-on socket 404s and retries in a loop.
   * Set `true` when running uvicorn (+ redis) to use the realtime bell. The
   * deployment passes it as `true`.
   */
  VITE_ENABLE_WEBSOCKET: z
    .string()
    .default('false')
    .transform((value) => value === 'true'),
});

export type Env = z.infer<typeof envSchema>;

/** Formats issues identically wherever validation runs. */
export function formatEnvIssues(error: z.ZodError): string {
  return error.issues.map((issue) => `  · ${issue.path.join('.')}: ${issue.message}`).join('\n');
}
